import React, { ErrorInfo, ReactNode, useEffect, useState } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Internal functional component to handle hooks (useEffect for countdown)
const ErrorFallback = ({ error }: { error: Error | null }) => {
  const [countdown, setCountdown] = useState(5);

  // Cache locale match once to avoid repeated regex
  const localeMatch = window.location.pathname.match(/^\/(zh-hk|zh-cn|en)/);
  const localeRoot = localeMatch ? localeMatch[0] : '/en';
  const isHome =
    window.location.pathname === '/' || /^\/(en|zh-hk|zh-cn)\/?$/.test(window.location.pathname);

  useEffect(() => {
    // Prevent infinite loop if home itself is crashing
    if (isHome) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = localeRoot;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isHome, localeRoot]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-natuvia-primary p-4 font-sans text-natuvia-text">
      <div className="max-w-md w-full bg-white border border-natuvia-text/10 shadow-lg rounded-2xl p-8 text-center">
        <div className="text-5xl mb-4">🔧</div>
        <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
        <p className="opacity-60 mb-6">
          We encountered an unexpected issue.
          {!isHome && (
            <span className="block mt-2">
              Redirecting to home in <span className="font-bold text-natuvia-cta">{countdown}</span>
              s...
            </span>
          )}
        </p>

        <button
          onClick={() => {
            window.location.href = localeRoot;
          }}
          className="bg-natuvia-cta hover:bg-opacity-90 text-white font-bold py-2 px-6 rounded-lg transition-colors w-full"
        >
          Go Home Now
        </button>

        <details className="mt-8 text-left border-t border-natuvia-text/5 pt-4">
          <summary className="text-xs opacity-40 cursor-pointer hover:opacity-100 transition-opacity">
            Technical Details
          </summary>
          <div className="bg-red-50 text-red-800 text-xs p-3 rounded mt-2 overflow-auto max-h-32 font-mono border border-red-100">
            {error?.message || 'Unknown Error'}
          </div>
        </details>
      </div>
    </div>
  );
};

export class ErrorBoundary extends React.Component<Props, State> {
  readonly props!: Props;

  state: State = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[Iron Dome] Caught error:', error, errorInfo);

    void import('../../lib/sentry')
      .then(({ captureException }) => {
        captureException(error, { componentStack: errorInfo.componentStack });
      })
      .catch(() => {
        // Keep the fallback UI independent from monitoring availability.
      });
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}
